import React from 'react';
import { useLocation } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Repeat, Shuffle, ChevronUp, ChevronDown, X, Heart, ListMusic, Radio } from 'lucide-react';
import { usePlayer } from '../../context/PlayerContext';
import { useLang } from '../../context/LanguageContext';

function formatTime(sec) {
  if (!sec || isNaN(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function GlobalPlayer() {
  const {
    currentSong, isPlaying, currentTime, duration, volume,
    shuffle, repeat, autoplay, expanded, queue, queueIndex,
    togglePlay, handleNext, handlePrev, seek, setVolume,
    toggleShuffle, toggleRepeat, toggleAutoplay, setExpanded, removeFromQueue, clearPlayer
  } = usePlayer();
  const { t, lang } = useLang();
  const location = useLocation();

  if (!currentSong) return null;

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const title = lang === 'en' ? (currentSong.title_en || currentSong.title_es) : currentSong.title_es;
  const isInternalLayout = location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/analytics');

  if (expanded) {
    return (
      <div
        data-testid="full-player"
        className="fixed inset-0 z-[60] bg-pulse-bg flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <button
            data-testid="collapse-player"
            onClick={() => setExpanded(false)}
            className="p-2 rounded-lg hover:bg-white/5"
            aria-label={t('common.close')}
          >
            <ChevronDown size={24} />
          </button>
          <span className="text-xs text-pulse-muted uppercase tracking-widest">{t('player.now_playing')}</span>
          <button className="p-2 rounded-lg hover:bg-white/5">
            <ListMusic size={20} />
          </button>
        </div>

        {/* Cover Art */}
        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-sm aspect-square rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={currentSong.cover}
              alt={title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Song Info */}
        <div className="px-8 py-4">
          <h3 data-testid="full-player-song-title" className="font-heading font-bold text-xl truncate">{title}</h3>
          <p data-testid="full-player-artist-name" className="text-pulse-muted text-sm">{currentSong.artist_name}</p>
        </div>

        {/* Progress Bar */}
        <div className="px-8 pb-2">
          <div
            className="w-full h-1.5 bg-pulse-elevated rounded-full cursor-pointer relative group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const pct = (e.clientX - rect.left) / rect.width;
              seek(pct * duration);
            }}
            role="slider"
            aria-label="Seek"
            aria-valuenow={currentTime}
            aria-valuemax={duration}
          >
            <div
              className="h-full bg-pulse-primary rounded-full transition-all duration-100 relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg" />
            </div>
          </div>
          <div className="flex justify-between mt-1 text-xs text-pulse-muted">
            <span data-testid="player-current-time">{formatTime(currentTime)}</span>
            <span data-testid="player-duration">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-8 pb-4">
          <button
            data-testid="shuffle-btn"
            onClick={toggleShuffle}
            className={`p-2 rounded-full transition-colors duration-200 ${shuffle ? 'text-pulse-primary' : 'text-pulse-muted hover:text-white'}`}
            aria-label={t('player.shuffle')}
          >
            <Shuffle size={20} />
          </button>
          <button
            data-testid="prev-btn"
            onClick={handlePrev}
            className="p-2 text-white hover:text-pulse-primary transition-colors duration-200"
            aria-label="Previous"
          >
            <SkipBack size={24} fill="currentColor" />
          </button>
          <button
            data-testid="play-pause-btn"
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform duration-200"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={28} className="text-black" fill="black" /> : <Play size={28} className="text-black ml-1" fill="black" />}
          </button>
          <button
            data-testid="next-btn"
            onClick={handleNext}
            className="p-2 text-white hover:text-pulse-primary transition-colors duration-200"
            aria-label="Next"
          >
            <SkipForward size={24} fill="currentColor" />
          </button>
          <button
            data-testid="autoplay-btn"
            onClick={toggleAutoplay}
            className={`p-2 rounded-full transition-colors duration-200 ${autoplay ? 'text-pulse-primary' : 'text-pulse-muted hover:text-white'}`}
            aria-label={lang === 'es' ? 'Autoplay' : 'Autoplay'}
          >
            <Radio size={20} />
          </button>
          <button
            data-testid="repeat-btn"
            onClick={toggleRepeat}
            className={`p-2 rounded-full transition-colors duration-200 relative ${repeat !== 'none' ? 'text-pulse-primary' : 'text-pulse-muted hover:text-white'}`}
            aria-label={t('player.repeat')}
          >
            <Repeat size={20} />
            {repeat === 'one' && <span className="absolute -top-1 -right-1 text-[8px] font-bold bg-pulse-primary text-black rounded-full w-3 h-3 flex items-center justify-center">1</span>}
          </button>
        </div>

        {/* Volume */}
        <div className="flex items-center gap-3 px-8 pb-6">
          <button onClick={() => setVolume(volume > 0 ? 0 : 0.8)} className="text-pulse-muted">
            {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="flex-1 h-1 accent-pulse-primary"
            aria-label="Volume"
          />
        </div>

        {/* Queue */}
        {queue.length > 0 && (
          <div className="px-8 pb-8 max-h-40 overflow-y-auto">
            <h4 className="text-xs text-pulse-muted uppercase tracking-wider mb-2">{t('player.queue')}</h4>
            {queue.slice(queueIndex + 1, queueIndex + 6).map((s, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <img src={s.cover} alt="" className="w-8 h-8 rounded object-cover" />
                  <div className="min-w-0">
                    <p className="truncate text-xs">{lang === 'en' ? (s.title_en || s.title_es) : s.title_es}</p>
                    <p className="truncate text-[10px] text-pulse-muted">{s.artist_name}</p>
                  </div>
                </div>
                <button onClick={() => removeFromQueue(queueIndex + 1 + i)} className="text-pulse-muted hover:text-white p-1">
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Mini Player
  return (
    <div
      data-testid="mini-player"
      className={`fixed left-0 right-0 z-50 glass border-t border-white/10 pb-[env(safe-area-inset-bottom)] ${isInternalLayout ? 'bottom-0' : 'bottom-16 md:bottom-0'}`}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-pulse-elevated">
        <div className="h-full bg-pulse-primary transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="flex items-center min-h-16 px-3 sm:px-4 gap-2 sm:gap-3">
        {/* Song info */}
        <button
          data-testid="expand-player"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-3 flex-1 min-w-0 text-left"
        >
          <img
            src={currentSong.cover}
            alt={title}
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
          />
          <div className="min-w-0">
            <p data-testid="mini-player-song-title" className="text-sm font-medium truncate">{title}</p>
            <p data-testid="mini-player-artist-name" className="text-xs text-pulse-muted truncate">{currentSong.artist_name}</p>
          </div>
        </button>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <button
            data-testid="mini-autoplay"
            onClick={toggleAutoplay}
            className={`p-1.5 transition-colors duration-200 ${autoplay ? 'text-pulse-primary' : 'text-pulse-muted hover:text-white'} hidden sm:block`}
            aria-label="Autoplay"
          >
            <Radio size={15} />
          </button>
          <button
            data-testid="mini-prev"
            onClick={handlePrev}
            className="p-1.5 text-pulse-muted hover:text-white transition-colors duration-200 hidden sm:block"
          >
            <SkipBack size={16} fill="currentColor" />
          </button>
          <button
            data-testid="mini-play-pause"
            onClick={togglePlay}
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform duration-200"
          >
            {isPlaying ? <Pause size={16} className="text-black" fill="black" /> : <Play size={16} className="text-black ml-0.5" fill="black" />}
          </button>
          <button
            data-testid="mini-next"
            onClick={handleNext}
            className="p-1.5 text-pulse-muted hover:text-white transition-colors duration-200 hidden sm:block"
          >
            <SkipForward size={16} fill="currentColor" />
          </button>
          <button
            data-testid="mini-close-player"
            onClick={clearPlayer}
            className="p-1.5 text-pulse-muted hover:text-red-400 transition-colors duration-200"
            aria-label={lang === 'es' ? 'Cerrar reproductor' : 'Close player'}
          >
            <X size={16} />
          </button>
        </div>

        {/* Expand */}
        <button
          onClick={() => setExpanded(true)}
          className="p-1.5 text-pulse-muted hover:text-white transition-colors duration-200 md:hidden"
        >
          <ChevronUp size={18} />
        </button>

        {/* Desktop volume */}
        <div className="hidden md:flex items-center gap-2 w-28">
          <span data-testid="mini-autoplay-indicator" className={`text-[10px] font-medium ${autoplay ? 'text-pulse-primary' : 'text-pulse-muted'}`}>{autoplay ? 'Radio' : 'Queue'}</span>
          <Volume2 size={14} className="text-pulse-muted flex-shrink-0" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="flex-1 h-1 accent-pulse-primary"
            aria-label="Volume"
          />
        </div>
      </div>
    </div>
  );
}
